import { If, location, onCleanup, onMount, reactive } from 'cdui-js';
import { plusIcon } from '../../../assets/commonIcons';
import i18n from '../../../i18n';
import { bootDepositApp } from '../deposit/h5-deposit/appBridge';
import { BankCardView } from './Card';
import { IconApplyList } from './icons';
import { BankShell } from './Shell';
import {
  addUrl,
  bankListHasLoaded,
  clearFormBlock,
  detailUrl,
  fetchBoundBanks,
  fetchMarketBanks,
  getBankListCache,
  pendingListUrl,
  refreshCardNames,
  saveDetailItem,
  setBankListCache,
  setMarketBankCache,
  toBankCard,
  unwrapList,
  type BankCard,
} from './shared';
import './bank.css';

/** 按真实 bank-card 结构占位：logo + 两行文案 + 币种行 */
const BankCardSkeleton = () => (
  <div class="bank-card bank-card-skeleton difi-skeleton-animate" aria-busy="true">
    <div class="bank-card-left">
      <div class="bank-card-top">
        <div class="bank-skel-logo" />
        <div class="bank-card-info">
          <div class="bank-skel-line bank-skel-name" />
          <div class="bank-skel-line bank-skel-no" />
        </div>
      </div>
      <div class="bank-skel-line bank-skel-currency" />
    </div>
    <div class="bank-skel-arrow" />
  </div>
);

export const BankAccountList = () => {
  const cached = getBankListCache();
  const hasCache = bankListHasLoaded() && cached.length > 0;
  const state = reactive({
    loading: !hasCache,
    rows: cached,
  });

  let reqId = 0;

  const load = async (silent: boolean) => {
    const id = ++reqId;
    if (!silent) state.loading = true;
    try {
      const rows = unwrapList(await fetchBoundBanks()).map((item, index) => toBankCard(item, index));
      if (id !== reqId) return;
      setBankListCache(rows);
      state.rows = rows;
    } catch {
      if (id !== reqId) return;
      if (!silent) {
        setBankListCache([]);
        state.rows = [];
      }
    } finally {
      if (id === reqId) state.loading = false;
    }
  };

  const loadNames = async () => {
    try {
      setMarketBankCache(unwrapList(await fetchMarketBanks()));
      state.rows = refreshCardNames(state.rows);
      setBankListCache(state.rows);
    } catch {
      // 没有字典就先显示接口名
    }
  };

  onMount(() => {
    clearFormBlock();
    const silent = bankListHasLoaded() && state.rows.length > 0;
    if (!silent) state.loading = true;
    void bootDepositApp()
      .catch(() => {})
      .then(() => {
        void load(silent);
        void loadNames();
      });

    const onShow = () => {
      if (document.visibilityState !== 'visible' || state.loading) return;
      void load(true);
    };
    document.addEventListener('visibilitychange', onShow);
    onCleanup(() => document.removeEventListener('visibilitychange', onShow));
  });

  const openDetail = (item: BankCard) => {
    saveDetailItem(item);
    location.routeTo(detailUrl(item.rowId));
  };

  return (
    <BankShell
      title={i18n.BankAccount.Title}
      right={
        <button
          type="button"
          class="nav-right-btn"
          aria-label={i18n.BankList.AddListTitle}
          onclick={() => location.routeTo(pendingListUrl())}
        >
          <IconApplyList />
        </button>
      }
    >
      <div class="list">
        <div class="section-title">{i18n.BankAccount.MyBanks}</div>
        <If when={state.loading}>
          <div class="bank-list">
            <BankCardSkeleton />
            <BankCardSkeleton />
          </div>
        </If>
        <If when={!state.loading && !state.rows.length}>
          <div class="empty">{i18n.BankList.Empty}</div>
        </If>
        <If when={!state.loading && !!state.rows.length}>
          <div class="bank-list">
            {state.rows.map((item) => (
              <BankCardView
                item={item}
                showArrow
                showPending
                onClick={() => openDetail(item)}
              />
            ))}
          </div>
        </If>
        <button type="button" class="add-dashed" onclick={() => location.routeTo(addUrl())}>
          <img src={plusIcon} alt="" />
          {i18n.BankAccount.AddBank}
        </button>
      </div>
    </BankShell>
  );
};
